// PathChooser — the new "/" entry point.
// Two ends of one life. Visitors choose: elders (Margaret/John) or kids (Ella).
// Lea's quote bridges the two. Full-bleed cinematic dual-card.
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Play, Pause } from "@phosphor-icons/react";
import { ForgetMeNotIcon } from "../components/ForgetMeNotIcon";
import { BrandLockup } from "../components/BrandLockup";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

const LEA_IMG = "/brand/team/lea-salon.png";
// Product-mockup hero portals — lavender adult & pink kids editions.
// Both images live in /app/frontend/public so they load instantly without
// hitting object-storage cold reads.
const MARGARET_IMG = "/product-mockup-1.png";
const ELLA_IMG = "/product-mockup-kids-pink.png";

export default function PathChooser() {
    const { t, i18n } = useTranslation();
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);

    // Locale-matched Lea bridge audio — reuses existing hero narration.
    const LANG_AUDIO = { es: "/audio/lea-hero.es.mp3", it: "/audio/lea-hero.it.mp3", el: "/audio/lea-hero.el.mp3", zh: "/audio/lea-hero.zh.mp3", de: "/audio/lea-hero.de.mp3", fr: "/audio/lea-hero.fr.mp3", ja: "/audio/lea-hero.ja.mp3", pt: "/audio/lea-hero.pt.mp3", tl: "/audio/lea-hero.tl.mp3", hi: "/audio/lea-hero.hi.mp3", ar: "/audio/lea-hero.ar.mp3" };
    const audioSrc = LANG_AUDIO[i18n.language] || "/audio/lea-hero.mp3";

    // Remember the visitor's last choice so they land on it next time.
    useEffect(() => {
        try { localStorage.setItem("reminissing.last_path", "chooser"); } catch (_) {}
    }, []);

    function toggleAudio() {
        if (!audioRef.current) return;
        if (playing) audioRef.current.pause();
        else audioRef.current.play().catch(() => {});
    }

    function rememberPath(path) {
        try { localStorage.setItem("reminissing.last_path", path); } catch (_) {}
    }

    return (
        <div
            className="relative min-h-screen bg-[#FDFBF7] text-[#2D2B2A] overflow-hidden"
            data-testid="path-chooser"
        >
            {/* Top bar — minimal: logo + language */}
            <header className="absolute top-0 inset-x-0 z-40 px-6 lg:px-10 pt-6 flex items-center justify-between">
                <div data-testid="chooser-logo">
                    <BrandLockup size="sm" />
                </div>
                <LanguageSwitcher />
            </header>

            {/* Floating petals decor — subtle */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-[18%] left-[6%] opacity-30 animate-float-gentle">
                    <ForgetMeNotIcon size={40} />
                </div>
                <div className="absolute top-[12%] right-[8%] opacity-25 animate-float-gentle" style={{ animationDelay: "1.5s" }}>
                    <ForgetMeNotIcon size={32} />
                </div>
                <div className="absolute bottom-[12%] left-[12%] opacity-20 animate-float-gentle" style={{ animationDelay: "2.4s" }}>
                    <ForgetMeNotIcon size={48} />
                </div>
                <div className="absolute bottom-[18%] right-[14%] opacity-25 animate-float-gentle" style={{ animationDelay: "0.7s" }}>
                    <ForgetMeNotIcon size={36} />
                </div>
            </div>

            <main className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 lg:pt-36">
                {/* Eyebrow + headline */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-10"
                >
                    <p
                        className="font-sans text-xs uppercase tracking-[0.32em] text-[#6B7DB8] font-semibold mb-5"
                        data-testid="chooser-eyebrow"
                    >
                        {t("path_chooser.eyebrow")}
                    </p>
                    <h1
                        className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2B2A] leading-[1.08] tracking-tight max-w-3xl mx-auto"
                        data-testid="chooser-headline"
                    >
                        {t("path_chooser.headline_part1")}{" "}
                        <em className="italic text-[#4A5A95]">{t("path_chooser.headline_emphasis")}</em>
                    </h1>
                    <p
                        className="mt-6 max-w-xl mx-auto text-base sm:text-lg text-[#5C5A59] font-light"
                        data-testid="chooser-intro"
                    >
                        {t("path_chooser.intro")}
                    </p>
                </motion.div>

                {/* Lea's bridge quote — small, italic, with optional audio toggle */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.2 }}
                    className="max-w-3xl mx-auto mb-14 text-center"
                    data-testid="chooser-lea-bridge"
                >
                    <button
                        onClick={toggleAudio}
                        className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/70 border border-[#8C9ECB]/40 hover:border-[#6B7DB8] transition-colors text-sm text-[#5C5A59] mb-5"
                        data-testid="chooser-lea-audio-toggle"
                        aria-label={playing ? t("lea_story.audio_aria_pause") : t("lea_story.audio_aria_play")}
                    >
                        <span className="w-7 h-7 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] flex items-center justify-center text-white">
                            {playing ? <Pause size={12} weight="fill" /> : <Play size={12} weight="fill" />}
                        </span>
                        <span>{playing ? t("lea_story.audio_speaking") : t("lea_story.audio_play")}</span>
                    </button>
                    <audio
                        key={audioSrc}
                        ref={audioRef}
                        src={audioSrc}
                        preload="metadata"
                        onPlay={() => setPlaying(true)}
                        onPause={() => setPlaying(false)}
                        onEnded={() => setPlaying(false)}
                        data-testid="chooser-lea-audio"
                    />
                    <p className="font-serif italic text-lg sm:text-xl text-[#2D2B2A] leading-relaxed text-balance">
                        {t("path_chooser.lea_quote")}
                    </p>
                    <p className="mt-4 text-xs uppercase tracking-[0.28em] text-[#8A8786]">
                        {t("path_chooser.lea_signature")}
                    </p>
                </motion.div>

                {/* The two choices */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10 max-w-6xl mx-auto">
                    {/* ELDERS card */}
                    <motion.div
                        initial={{ opacity: 0, x: -22 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        data-testid="chooser-card-elders"
                    >
                        <Link
                            to="/adults"
                            onClick={() => rememberPath("/adults")}
                            className="group block relative overflow-hidden rounded-[2rem] border border-[#8C9ECB]/30 hover:border-[#6B7DB8] shadow-rem-soft hover:shadow-rem-card transition-all duration-500 hover:-translate-y-1 bg-gradient-to-br from-[#F4F6FB] via-white to-[#EDF0F8]"
                            data-testid="chooser-cta-elders"
                        >
                            <div className="relative aspect-[4/3] overflow-hidden">
                                <img
                                    src={MARGARET_IMG}
                                    alt="The lavender remembr tablet — for elders, dementia care and reminiscence therapy"
                                    className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1200ms]"
                                    loading="eager"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1c25]/70 via-transparent to-transparent" />
                                <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/85 backdrop-blur text-[10px] font-semibold uppercase tracking-[0.22em] text-[#4A5A95]">
                                    <ForgetMeNotIcon size={12} />
                                    {t("path_chooser.elders.tag")}
                                </div>
                            </div>
                            <div className="p-7 sm:p-9">
                                <p className="font-sans text-[10px] uppercase tracking-[0.28em] text-[#6B7DB8] font-semibold mb-3">
                                    {t("path_chooser.elders.eyebrow")}
                                </p>
                                <h2 className="font-serif text-2xl sm:text-3xl text-[#2D2B2A] leading-tight">
                                    {t("path_chooser.elders.title")}
                                </h2>
                                <p className="mt-3 text-[#5C5A59] leading-relaxed text-sm sm:text-base">
                                    {t("path_chooser.elders.subtitle")}
                                </p>
                                <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#4A5A95] group-hover:text-[#2D2B2A] transition-colors">
                                    {t("path_chooser.elders.cta")}
                                    <ArrowRight weight="bold" size={16} className="group-hover:translate-x-1 transition-transform" />
                                </div>
                            </div>
                        </Link>
                    </motion.div>

                    {/* KIDS card */}
                    <motion.div
                        initial={{ opacity: 0, x: 22 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        data-testid="chooser-card-kids"
                    >
                        <Link
                            to="/kids"
                            onClick={() => rememberPath("/kids")}
                            className="group block relative overflow-hidden rounded-[2rem] border border-[#F4C7CC]/50 hover:border-[#E89AA1] shadow-rem-soft hover:shadow-rem-card transition-all duration-500 hover:-translate-y-1 bg-gradient-to-br from-[#FBF1F2] via-white to-[#EAF2F9]"
                            data-testid="chooser-cta-kids"
                        >
                            <div className="relative aspect-[4/3] overflow-hidden">
                                <img
                                    src={ELLA_IMG}
                                    alt="The pink remembr kids tablet with knit booties and teddy bear — a baby-girl edition"
                                    className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1200ms]"
                                    loading="eager"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#A85D62]/40 via-transparent to-transparent" />
                                <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/85 backdrop-blur text-[10px] font-semibold uppercase tracking-[0.22em] text-[#A85D62]">
                                    <ForgetMeNotIcon size={12} />
                                    {t("path_chooser.kids.tag")}
                                </div>
                            </div>
                            <div className="p-7 sm:p-9">
                                <p className="font-sans text-[10px] uppercase tracking-[0.28em] text-[#A85D62] font-semibold mb-3">
                                    {t("path_chooser.kids.eyebrow")}
                                </p>
                                <h2 className="font-serif text-2xl sm:text-3xl text-[#2D2B2A] leading-tight">
                                    {t("path_chooser.kids.title")}
                                </h2>
                                <p className="mt-3 text-[#5C5A59] leading-relaxed text-sm sm:text-base">
                                    {t("path_chooser.kids.subtitle")}
                                </p>
                                <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#A85D62] group-hover:text-[#2D2B2A] transition-colors">
                                    {t("path_chooser.kids.cta")}
                                    <ArrowRight weight="bold" size={16} className="group-hover:translate-x-1 transition-transform" />
                                </div>
                            </div>
                        </Link>
                    </motion.div>
                </div>

                {/* Footnote + Lea portrait sliver */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.9 }}
                    className="mt-16 flex flex-col items-center gap-4 text-center"
                >
                    <div className="rounded-full overflow-hidden w-14 h-14 border-2 border-white shadow-rem-soft">
                        <img src={LEA_IMG} alt="Lea Millen, founder" className="w-full h-full object-cover object-top" />
                    </div>
                    <p className="text-sm text-[#8A8786] max-w-md" data-testid="chooser-footnote">
                        {t("path_chooser.footnote")}
                    </p>
                </motion.div>
            </main>
        </div>
    );
}
